import { AirplaneTiltIcon, PencilSimpleIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { useState } from 'react';

import { EmptyState } from '@/components/empty-state';
import { ICON_WEIGHT } from '@/components/icon';
import { Button } from '@/components/ui/button';
import type { Aircraft } from '@/lib/db';
import { aircraft as aircraftRepo } from '@/lib/db';

import { categoryLabel } from './classes';

/**
 * The aircraft register — `PLAN.md` §6 Phase 4: "registration, type, ICAO
 * type code, class, engine count, variant notes". A flight links to one of
 * these by registration; the logbook (Phase 5) is what actually totals hours
 * against it.
 */

function AircraftRow({ record }: { record: Aircraft }) {
  const [confirming, setConfirming] = useState(false);
  const typeLine = [record.make, record.model].filter((part) => part !== undefined).join(' ');

  return (
    <li className="border-border bg-surface rounded-container border px-4 py-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-sm font-semibold tracking-tight">{record.registration}</span>
        <span className="text-muted-foreground text-sm">
          {typeLine === '' ? categoryLabel(record.category) : typeLine}
        </span>
      </div>

      <dl className="text-muted-foreground mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs">
        <div className="flex gap-1.5">
          <dt>Category</dt>
          <dd className="text-foreground">{categoryLabel(record.category)}</dd>
        </div>
        {record.icaoType === undefined ? null : (
          <div className="flex gap-1.5">
            <dt>ICAO type</dt>
            <dd className="text-foreground">{record.icaoType}</dd>
          </div>
        )}
        <div className="flex gap-1.5">
          <dt>Engines</dt>
          <dd className="tabular text-foreground">{record.engineCount}</dd>
        </div>
        {record.isSimulator ? <dd className="text-foreground">Synthetic trainer</dd> : null}
      </dl>

      {record.notes === undefined ? null : (
        <p className="text-muted-foreground mt-2 text-xs leading-relaxed">{record.notes}</p>
      )}

      <div className="mt-3">
        {confirming ? (
          <div className="flex items-center gap-2">
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                void aircraftRepo.delete(record.id);
              }}
            >
              Remove permanently
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setConfirming(false);
              }}
            >
              Keep
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to="/aircraft/$aircraftId" params={{ aircraftId: record.id }}>
                <PencilSimpleIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
                Flights &amp; details
              </Link>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Remove ${record.registration}`}
              onClick={() => {
                setConfirming(true);
              }}
            >
              <TrashIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
              Remove
            </Button>
          </div>
        )}
      </div>
    </li>
  );
}

export function AircraftList() {
  const rows = useLiveQuery(() => aircraftRepo.list(), [], null);

  if (rows === null) {
    return (
      <p className="text-muted-foreground text-sm" data-testid="aircraft-list-loading">
        Opening the local database…
      </p>
    );
  }

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={AirplaneTiltIcon}
        title="No aircraft"
        description="Add an aircraft to link flights to its registration, type and class."
      >
        <Button asChild>
          <Link to="/aircraft/new">
            <PlusIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
            Add aircraft
          </Link>
        </Button>
      </EmptyState>
    );
  }

  return (
    <ul className="space-y-3" data-testid="aircraft-list">
      {rows.map((record) => (
        <AircraftRow key={record.id} record={record} />
      ))}
    </ul>
  );
}
